/**
 * Color maths for the branding engine.
 *
 * Two jobs:
 *   1. Derive tokens (hover, pressed, chip background, text-on-brand, focus ring)
 *      so one color input produces a complete, always-readable button system.
 *   2. Block an org from saving an unreadable theme — guardrail #1 from D-02.
 */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
export function isHexColor(value) {
    return HEX_RE.test(value);
}
export function hexToRgb(hex) {
    if (!isHexColor(hex))
        throw new Error(`Not a 6-digit hex color: ${hex}`);
    return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
    };
}
export function rgbToHex({ r, g, b }) {
    const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
    const hex = (n) => clamp(n).toString(16).padStart(2, "0");
    return `#${hex(r)}${hex(g)}${hex(b)}`.toUpperCase();
}
/** Blend `amount` (0-1) of `b` into `a`. */
export function mix(a, b, amount) {
    const A = hexToRgb(a);
    const B = hexToRgb(b);
    const t = Math.max(0, Math.min(1, amount));
    return rgbToHex({
        r: A.r + (B.r - A.r) * t,
        g: A.g + (B.g - A.g) * t,
        b: A.b + (B.b - A.b) * t,
    });
}
export function darken(hex, amount) {
    return mix(hex, "#000000", amount);
}
export function lighten(hex, amount) {
    return mix(hex, "#FFFFFF", amount);
}
/** `#C54E1B` + 0.35 -> `rgb(197 78 27 / 0.35)`. */
export function alpha(hex, a) {
    const { r, g, b } = hexToRgb(hex);
    return `rgb(${r} ${g} ${b} / ${Math.max(0, Math.min(1, a))})`;
}
/** WCAG 2.1 relative luminance. */
export function relativeLuminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    const channel = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** WCAG 2.1 contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const light = Math.max(la, lb);
    const dark = Math.min(la, lb);
    return (light + 0.05) / (dark + 0.05);
}
export const WCAG_AA_NORMAL = 4.5;
export const WCAG_AA_LARGE = 3;
export const INK_LIGHT = "#FFFFFF";
export const INK_DARK = "#1C1714";
/** Whichever of white / near-black is readable on `background`. */
export function bestTextOn(background) {
    return contrastRatio(background, INK_DARK) >= contrastRatio(background, INK_LIGHT)
        ? INK_DARK
        : INK_LIGHT;
}
/**
 * Return `color` if it already reads against `against`, otherwise the nearest
 * shade of it that does.
 *
 * This is what lets an organization keep its exact brand color as a FILL
 * (buttons, bars, chips) while text drawn in that color is automatically
 * nudged until it is readable — rather than the org being blocked from using
 * their own brand color at all.
 *
 * The GatiQ brand itself needs this: #C54E1B on #FAFAFA measures 4.49:1,
 * which misses WCAG AA by 0.01.
 */
export function ensureContrast(color, against, required = WCAG_AA_NORMAL) {
    if (!isHexColor(color) || !isHexColor(against))
        return color;
    if (contrastRatio(color, against) >= required)
        return color;
    return suggestPassingColor(color, against, required) ?? bestTextOn(against);
}
/**
 * Nudge `color` toward black or white — whichever direction increases contrast
 * against `against` — until it passes, or return null if it cannot.
 */
export function suggestPassingColor(color, against, required = WCAG_AA_NORMAL) {
    const towardDark = relativeLuminance(against) > relativeLuminance(color);
    for (let step = 1; step <= 20; step++) {
        const amount = step * 0.05;
        const candidate = towardDark ? darken(color, amount) : lighten(color, amount);
        if (contrastRatio(candidate, against) >= required)
            return candidate;
    }
    return null;
}
/**
 * Guardrail #1. Checks every pair that will actually sit on top of each other
 * in the UI. Returns an empty array when the theme is safe to save.
 */
export function validateContrast(branding) {
    const issues = [];
    const check = (fg, bg, pair, label, required = WCAG_AA_NORMAL) => {
        const fgValue = branding[fg];
        const bgValue = branding[bg];
        if (!fgValue || !bgValue || !isHexColor(fgValue) || !isHexColor(bgValue))
            return;
        const ratio = contrastRatio(fgValue, bgValue);
        if (ratio < required) {
            issues.push({
                pair,
                label,
                ratio: Math.round(ratio * 100) / 100,
                required,
                suggestion: suggestPassingColor(fgValue, bgValue, required),
            });
        }
    };
    /*
     * `color1` is deliberately NOT checked against `color2`.
     *
     * The brand color is only ever painted as a FILL. Text sitting on it uses
     * the derived `color1Text`, and text drawn IN the brand color on the page
     * surface uses the derived `color1Ink` — both guaranteed readable by
     * construction (see `deriveTokens`). Checking the raw fill here would block
     * legitimate brand colors, including GatiQ's own #C54E1B at 4.49:1, without
     * making anything more readable.
     *
     * Everything below IS user-settable text, so it is checked.
     */
    // Muted text is the smallest text in the app — it must clear AA.
    check("colorTextMuted", "color2", ["colorTextMuted", "color2"], "Muted text on page background");
    // Status text sits on the page surface too. Large-text threshold: these are
    // used on badges and headings, not body copy.
    check("colorSuccess", "color2", ["colorSuccess", "color2"], "Success color on background", WCAG_AA_LARGE);
    check("colorWarning", "color2", ["colorWarning", "color2"], "Warning color on background", WCAG_AA_LARGE);
    check("colorError", "color2", ["colorError", "color2"], "Error color on background", WCAG_AA_LARGE);
    check("colorInfo", "color2", ["colorInfo", "color2"], "Info color on background", WCAG_AA_LARGE);
    return issues;
}
//# sourceMappingURL=contrast.js.map